import React, { useMemo } from "react";
import {
  Route as ModernRoute,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";

export { BrowserRouter, Link, NavLink, useLocation, useParams } from "react-router-dom";

export function useHistory() {
  const navigate = useNavigate();
  const location = useLocation();

  return useMemo(
    () => {
      const navigateWithLegacyState = (to, options = {}) => {
        if (to && typeof to === "object" && "state" in to) {
          const { state, ...path } = to;
          navigate(path, { ...options, state: options.state ?? state });
          return;
        }
        navigate(to, options);
      };

      return {
        location,
        push: (to, state) => navigateWithLegacyState(to, { state }),
        replace: (to, state) =>
          navigateWithLegacyState(to, { replace: true, state }),
        go: (delta) => navigate(delta),
        goBack: () => navigate(-1),
        goForward: () => navigate(1),
      };
    },
    [location, navigate],
  );
}

function LegacyRouteElement({ component: Component, render, children }) {
  const history = useHistory();
  const location = useLocation();
  const params = useParams();
  const routeProps = {
    history,
    location,
    match: { params, path: "", url: location.pathname, isExact: true },
  };

  if (Component) return <Component {...routeProps} />;
  if (render) return render(routeProps);
  if (typeof children === "function") return children(routeProps);
  return children || null;
}

export function Route({ component, render, children, exact, ...routeProps }) {
  return (
    <ModernRoute
      {...routeProps}
      element={
        <LegacyRouteElement
          component={component}
          render={render}
          children={children}
        />
      }
    />
  );
}

export function Switch({ children }) {
  return (
    <Routes>
      {React.Children.map(children, (child) => {
        if (!React.isValidElement(child) || child.type !== Route) return child;

        const {
          component,
          render,
          children: routeChildren,
          exact,
          ...routeProps
        } = child.props;
        return (
          <ModernRoute
            {...routeProps}
            element={
              <LegacyRouteElement
                component={component}
                render={render}
                children={routeChildren}
              />
            }
          />
        );
      })}
    </Routes>
  );
}

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const history = useHistory();
    const location = useLocation();
    const params = useParams();

    return (
      <Component
        {...props}
        history={history}
        location={location}
        match={{ params, path: "", url: location.pathname, isExact: true }}
      />
    );
  }

  ComponentWithRouterProp.displayName = `withRouter(${
    Component.displayName || Component.name || "Component"
  })`;
  return ComponentWithRouterProp;
}
